import { Search, X } from 'lucide-react';
import { FilterLista, FilterSelect } from '@/components/patterns/filter-bar';
import { ChipPeriodo, FiltroPeriodo } from '@/components/patterns/filtro-periodo';
import { StatusIcone } from '@/components/patterns/status-badge';
import { Button } from '@/components/ui/button';
import { Field, Input } from '@/components/ui/field';
import { Switch } from '@/components/ui/switch';
import { PRODUTOS, STATUS_BUILD } from '@/data';
import { ROTULO_STATUS } from '@/lib/capabilities';
import type { FiltrosFeedEstado } from './use-filtros-feed';

export interface OpcaoFiltro {
  valor: string;
  rotulo: string;
}

export interface OpcoesFiltros {
  ramoFixo: boolean;
  /** Sem acesso a Ramos o seletor de ramo não existe (a lista dele daria 403). */
  mostrarRamo: boolean;
  opcoesRamo: OpcaoFiltro[];
  opcoesBranch: OpcaoFiltro[];
}

/**
 * Filtros do feed em COLUNA (lateral do Timeline e Sheet do celular): campos
 * de largura total, espaçamento uniforme (gap-5), status como lista vertical
 * de linhas — nada quebra nem é cortado em 20rem nem em 390px. Só um dos dois
 * lugares é montado por vez, para os ids não se repetirem.
 */
export function PainelFiltros({
  prefixo,
  f,
  opcoes,
}: {
  prefixo: string;
  f: FiltrosFeedEstado;
  opcoes: OpcoesFiltros;
}) {
  const { ramoFixo, mostrarRamo, opcoesRamo, opcoesBranch } = opcoes;
  const comRamo = mostrarRamo && !ramoFixo;
  return (
    <div role="search" aria-label="Filtros do feed" className="flex flex-col gap-5">
      <Field id={`${prefixo}-busca`} label="Buscar versão">
        {(props) => (
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-icon" aria-hidden="true" />
            <Input
              {...props}
              type="search"
              value={f.busca}
              placeholder="2.2602.78 ou 4510"
              onChange={(evento) => f.setBusca(evento.target.value)}
              className="pl-9"
            />
          </div>
        )}
      </Field>

      <FilterSelect
        id={`${prefixo}-produto`}
        label="Produto"
        valor={f.produto}
        aoMudar={(valor) => f.atualizar({ produto: valor })}
        opcoes={Object.entries(PRODUTOS).map(([valor, rotulo]) => ({ valor, rotulo }))}
        className="min-w-0"
      />

      <div className="grid grid-cols-2 gap-3">
        {comRamo ? (
          <FilterSelect
            id={`${prefixo}-ramo`}
            label="Ramo"
            valor={f.ramo}
            aoMudar={(valor) => f.atualizar({ ramo: valor, branch: null })}
            opcoes={opcoesRamo}
            className="min-w-0"
          />
        ) : null}
        <FilterSelect
          id={`${prefixo}-branch`}
          label="Branch"
          valor={f.branch}
          aoMudar={(valor) => f.atualizar({ branch: valor })}
          opcoes={opcoesBranch}
          rotuloTodos="Todas"
          className={comRamo ? 'min-w-0' : 'col-span-2 min-w-0'}
        />
      </div>

      <div className="flex flex-col gap-2">
        <FiltroPeriodo
          id={`${prefixo}-periodo`}
          de={f.periodo.de}
          ate={f.periodo.ate}
          aoMudar={(p) => f.atualizar({ de: p.de, ate: p.ate })}
        />
        <ChipPeriodo de={f.periodo.de} ate={f.periodo.ate} aoLimpar={() => f.atualizar({ de: null, ate: null })} className="self-start" />
      </div>

      <FilterLista
        id={`${prefixo}-status`}
        label="Status"
        valores={f.status}
        aoMudar={(valores) => f.atualizar({ status: valores })}
        opcoes={STATUS_BUILD.map((valor) => ({ valor, rotulo: ROTULO_STATUS[valor], icone: <StatusIcone status={valor} /> }))}
      />

      {f.podeIncidentes ? (
        <label className="flex cursor-pointer items-center justify-between gap-3 text-label" htmlFor={`${prefixo}-incidente`}>
          <span className="flex min-w-0 flex-col">
            Com incidente
            <span className="text-cell font-normal text-on-surface-muted">Só versões com incidente aberto ou mitigado</span>
          </span>
          <Switch id={`${prefixo}-incidente`} checked={f.comIncidente} onCheckedChange={(valor) => f.atualizar({ com_incidente: valor ? 'true' : null })} />
        </label>
      ) : null}

      {f.quantidade > 0 ? (
        <Button variant="outline" size="sm" onClick={f.limpar} className="w-full">
          <X aria-hidden="true" />
          Limpar {f.quantidade === 1 ? '1 filtro' : `${f.quantidade} filtros`}
        </Button>
      ) : null}
    </div>
  );
}
